"use client";

import Link from "next/link";
import { ChevronRight, Lock } from "lucide-react";
import { useScrollReveal } from "@/hooks/common/useScrollReveal";
import GcsImage from "./GcsImage";
import { hiw1, hiw2, hiw3 } from "@/lib/gcsImagePaths";
import { cn } from "@/lib/utils";
import { hiw1Dim, hiw2Dim, hiw3Dim } from "@/lib/salesData";
import { Button } from "../ui/button";
import { navigateWithQueryParams } from "@/utils/navigationUtils";

import { useRouter } from "next/navigation";

interface HowItWorksSectionProps {
  expertHelpText?: boolean;
  bestQuotesText?: string;
  background?: string;
}

export default function HowItWorksSection({
  expertHelpText,
  background,
}: HowItWorksSectionProps) {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal();
  const router = useRouter();
  const { ref: howItWorksItem1Ref, isVisible: howItWorksItem1IsVisible } =
    useScrollReveal();
  const { ref: howItWorksItem2Ref, isVisible: howItWorksItem2IsVisible } =
    useScrollReveal();
  const { ref: howItWorksItem3Ref, isVisible: howItWorksItem3IsVisible } =
    useScrollReveal();

  return (
    <section
      ref={sectionRef}
      className={cn(
        `py-16 md:py-24 bg-secondary transition-opacity duration-700 will-change-[opacity] ${
          sectionVisible ? "opacity-100" : "opacity-0"
        }`,
        background,
      )}
      style={{
        visibility: sectionVisible ? "visible" : "hidden",
      }}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            How It Works
          </h2>
          <p className="text-base md:text-lg text-muted-foreground">
            Shopping is easy — just call, email, or text us to start saving.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          {/* Connecting dashed line */}
          <div className="hidden md:block absolute top-[100px] left-[15%] right-[15%] h-0 border-t-2 border-dashed border-muted-foreground/30" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
            {/* Step 1 */}
            <div
              ref={howItWorksItem1Ref}
              className={`text-center transition-opacity duration-700 will-change-[opacity] ${
                howItWorksItem1IsVisible ? "opacity-100" : "opacity-0"
              }`}
              style={{
                visibility: howItWorksItem1IsVisible ? "visible" : "hidden",
              }}
            >
              <div className="relative w-64 h-64 mx-auto mb-8">
                <GcsImage
                  path={hiw1}
                  width={hiw1Dim.width}
                  height={hiw1Dim.height}
                  alt="Tell us about you"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3 uppercase tracking-wide">
                Share a few details
              </h3>
              <p className="text-muted-foreground">
                Tell us a little about your home, vehicles, and current
                coverage.
              </p>
            </div>

            {/* Step 2 */}
            <div
              ref={howItWorksItem2Ref}
              className={`text-center transition-opacity duration-700 delay-200 will-change-[opacity] ${
                howItWorksItem2IsVisible ? "opacity-100" : "opacity-0"
              }`}
              style={{
                visibility: howItWorksItem2IsVisible ? "visible" : "hidden",
              }}
            >
              <div className="relative w-64 h-64 mx-auto mb-8">
                <GcsImage
                  path={hiw2}
                  width={hiw2Dim.width}
                  height={hiw2Dim.height}
                  alt="We shop the market"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3 uppercase tracking-wide">
                We shop for you
              </h3>
              <p className="text-muted-foreground">
                We compare quotes from 50+ carriers to find you the best
                options.
              </p>
            </div>

            {/* Step 3 */}
            <div
              ref={howItWorksItem3Ref}
              className={`text-center transition-opacity duration-700 delay-400 will-change-[opacity] ${
                howItWorksItem3IsVisible ? "opacity-100" : "opacity-0"
              }`}
              style={{
                visibility: howItWorksItem3IsVisible ? "visible" : "hidden",
              }}
            >
              <div className="relative w-64 h-64 mx-auto mb-8">
                <GcsImage
                  path={hiw3}
                  width={hiw3Dim.width}
                  height={hiw3Dim.height}
                  alt="We keep you covered"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3 uppercase tracking-wide">
                Switch in minutes
              </h3>
              <p className="text-muted-foreground">
                We&apos;ll help you switch to better coverage in as little as 15
                minutes.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button
              className="bg-[#FFB703] hover:bg-[#FFB703]/90 text-[#090739] font-semibold px-8 py-3 h-auto rounded-md"
              onClick={() => navigateWithQueryParams(router, "/compare")}
            >
              COMPARE QUOTES{" "}
              <ChevronRight className="ml-0 h-5 w-5" strokeWidth={3} />
            </Button>
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-muted-foreground">
              <Lock className="w-5 h-5" />
              <span>100% Secure & Confidential</span>
            </div>
          </div>

          {/* Expert Help Text */}
          {expertHelpText && (
            <div className="text-center mt-12">
              <p className="text-base text-muted-foreground">
                Need expert help?{" "}
                <Link
                  href="/careers/agents"
                  className="text-primary hover:text-primary/80 underline font-medium"
                >
                  Find an agent
                </Link>
                .
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
